import { USAGE_TYPES, MEDIA_CHANNELS } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { ScrollText, Loader2, Sparkles, Save } from "lucide-react";

const MediaChannelPicker = ({ job, setJob, disabled }) => (
  <div className="space-y-1.5">
    <Label>Where will the imagery be used? <span className="text-destructive">*</span></Label>
    <p className="text-[11px] text-muted-foreground">Select every media channel you intend to use. Undeclared channels are outside the agreement.</p>
    <div className="grid gap-2 sm:grid-cols-2">
      {MEDIA_CHANNELS.map((c) => {
        const checked = job.media_channels.includes(c.value);
        return (
          <button
            key={c.value}
            type="button"
            data-testid={`job-media-channel-${c.value}`}
            disabled={disabled}
            onClick={() =>
              setJob({
                ...job,
                media_channels: checked
                  ? job.media_channels.filter((v) => v !== c.value)
                  : [...job.media_channels, c.value],
              })
            }
            className={`flex items-start gap-2.5 rounded-xl border p-3 text-left transition-[border-color,background-color] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-70 ${
              checked ? "border-[hsl(174,38%,34%)] bg-[hsl(174,38%,95%)]" : "bg-card hover:bg-muted"
            }`}
          >
            <span className={`mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded border text-[10px] font-bold ${
              checked ? "border-[hsl(174,38%,34%)] bg-[hsl(174,38%,34%)] text-white" : "border-input bg-card"
            }`}>
              {checked ? "\u2713" : ""}
            </span>
            <span>
              <span className="block text-sm font-medium">{c.label}</span>
              <span className="block text-[11px] text-muted-foreground">{c.hint}</span>
            </span>
          </button>
        );
      })}
    </div>
  </div>
);

export const JobDescriptionForm = ({ job, setJob, approved, screeningBusy, savingDraft, onScreen, onSaveDraft }) => (
  <Card data-testid="booking-job-form" className="rounded-2xl">
    <CardHeader>
      <CardTitle className="flex items-center gap-2 text-base">
        <span className="flex h-6 w-6 items-center justify-center rounded-full bg-primary text-xs text-primary-foreground">1</span>
        Describe the job
      </CardTitle>
    </CardHeader>
    <CardContent className="space-y-4">
      <div data-testid="booking-scope-notice" className="flex items-start gap-3 rounded-xl border-2 border-[hsl(43,74%,70%)] bg-[hsl(43,74%,95%)] p-4">
        <ScrollText className="mt-0.5 h-5 w-5 shrink-0 text-[hsl(43,60%,30%)]" />
        <div className="space-y-1 text-sm">
          <p className="font-semibold text-[hsl(43,60%,20%)]">Describe the media accurately — it defines your agreement</p>
          <p className="text-[hsl(43,50%,25%)]">
            Your licence is strictly limited to the media and channels you describe here. It is also what the AI
            uses to calculate the suggested fee. Anything you don't declare, you can't use — a different
            channel or campaign later requires a new booking and new consent from the model.
          </p>
        </div>
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="job-title">Job title</Label>
        <Input data-testid="job-title-input" id="job-title" placeholder="e.g. Summer capsule collection product imagery"
          value={job.title} onChange={(e) => setJob({ ...job, title: e.target.value })} className="rounded-xl bg-card" disabled={!!approved} />
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="job-desc">Job description</Label>
        <Textarea data-testid="job-description-input" id="job-desc" rows={4}
          placeholder="Describe exactly how and where this model's look will appear — which products, which media (e.g. product pages on your store, Instagram ads, printed catalogue), and what style of imagery your AI app will generate…"
          value={job.description} onChange={(e) => setJob({ ...job, description: e.target.value })} className="rounded-xl bg-card" disabled={!!approved} />
        <p className="text-[11px] font-medium text-[hsl(43,60%,30%)]">
          Be precise about the media — the licence covers only what you describe here, and the suggested fee is calculated from it.
        </p>
      </div>
      <MediaChannelPicker job={job} setJob={setJob} disabled={!!approved} />
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-1.5">
          <Label>Usage type</Label>
          <Select value={job.usage_type} onValueChange={(v) => setJob({ ...job, usage_type: v })} disabled={!!approved}>
            <SelectTrigger data-testid="job-usage-type-select" className="rounded-xl bg-card">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {USAGE_TYPES.map((u) => (
                <SelectItem key={u.value} value={u.value}>{u.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label>Licence duration</Label>
          <Select value={String(job.duration_months)} onValueChange={(v) => setJob({ ...job, duration_months: parseInt(v) })} disabled={!!approved}>
            <SelectTrigger data-testid="job-duration-select" className="rounded-xl bg-card">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {[1, 2, 3, 6, 12].map((m) => (
                <SelectItem key={m} value={String(m)}>{m} month{m > 1 ? "s" : ""}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="flex items-center justify-between rounded-xl border bg-card p-3">
          <div>
            <p className="text-sm font-medium">Exclusive usage</p>
            <p className="text-[11px] text-muted-foreground">Model can't licence to others during the term</p>
          </div>
          <Switch data-testid="job-exclusivity-switch" checked={job.exclusivity} onCheckedChange={(v) => setJob({ ...job, exclusivity: v })} disabled={!!approved} />
        </div>
        <div className="flex items-center justify-between rounded-xl border bg-card p-3">
          <div>
            <p className="text-sm font-medium">Request retouching</p>
            <p className="text-[11px] text-muted-foreground">Minor only — needs the model's explicit consent</p>
          </div>
          <Switch data-testid="job-retouching-switch" checked={job.retouching_requested} onCheckedChange={(v) => setJob({ ...job, retouching_requested: v })} disabled={!!approved} />
        </div>
      </div>
      {!approved && (
        <div className="flex flex-wrap gap-2">
          <Button data-testid="booking-run-screening-button" onClick={onScreen} disabled={screeningBusy} className="rounded-xl" size="lg">
            {screeningBusy ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" /> AI is reviewing your job… (10–30s)
              </>
            ) : (
              <>
                <Sparkles className="mr-2 h-4 w-4" /> Run AI screening & get rate
              </>
            )}
          </Button>
          <Button data-testid="booking-save-draft-button" type="button" variant="outline" size="lg" disabled={savingDraft || !job.title}
            className="rounded-xl border bg-card hover:bg-muted" onClick={onSaveDraft}>
            {savingDraft ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
            Save draft
          </Button>
        </div>
      )}
    </CardContent>
  </Card>
);
